import api from './api';
import { NEAR_ME_REFRESH_MS, requestBrowserLocation } from './eventLocation';

export { NEAR_ME_REFRESH_MS as CURRENT_MARKET_REFRESH_MS };

export function isCurrentMarketMode(preferences) {
  return preferences?.locationPreferences?.followCurrentMarket === true;
}

export function currentMarketIsFresh(loc, now = Date.now()) {
  const lastUpdated = loc?.currentMarketUpdatedAt;
  if (!lastUpdated) return false;
  const age = now - new Date(lastUpdated).getTime();
  return Number.isFinite(age) && age >= 0 && age < NEAR_ME_REFRESH_MS;
}

/**
 * Resolve the browser's current country for Markets.
 * Does not store coordinates and does not update Event location.
 * When persist is true, writes last-known currentMarketCountry only.
 */
export async function resolveCurrentMarketCountry({ persist = false, force = false } = {}) {
  try {
    const coords = await requestBrowserLocation({
      maximumAge: force ? 60 * 1000 : undefined,
    });
    const res = await api.post('/api/user/resolve-market-country', {
      ...coords,
      persistCurrentMarket: persist === true,
    });
    const country = String(res.data?.country || '').trim();
    if (!country) return { status: 'error' };
    return {
      status: 'ok',
      country,
      currentMarketUpdatedAt: res.data?.currentMarketUpdatedAt || new Date().toISOString(),
    };
  } catch (err) {
    if (err && err.code === 1) return { status: 'denied' };
    if (err && (err.code === 2 || err.code === 3 || err.code === 'unavailable')) {
      return { status: 'unavailable' };
    }
    if (!err || !err.response) return { status: 'network' };
    if (err.response.status === 401 || err.response.status === 403) return { status: 'auth' };
    if (err.response.status === 422) return { status: 'error' };
    return { status: 'error' };
  }
}

/**
 * Refresh the last-known current-market country when the mode is on and stale.
 * Falls back to the cached country if geolocation or reverse-geocoding fails.
 */
export async function refreshCurrentMarketIfNeeded({ preferences, force = false } = {}) {
  let prefs = preferences;
  if (!prefs) {
    const res = await api.get('/api/user/insight-preferences');
    prefs = res.data?.preferences;
  }
  if (!isCurrentMarketMode(prefs)) return { status: 'skipped' };
  const loc = prefs.locationPreferences || {};
  const cached = String(loc.currentMarketCountry || '').trim();
  if (!force && currentMarketIsFresh(loc)) {
    return { status: 'fresh', country: cached };
  }

  const result = await resolveCurrentMarketCountry({ persist: true, force });
  if (result.status === 'ok') return result;
  return { ...result, country: cached };
}
